/* eslint-disable react-hooks/exhaustive-deps */
import './components/global/ChartJsConfig'; // Import the Chart.js configuration
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useMsal, useIsAuthenticated } from "@azure/msal-react";

import Header from './components/global/Header';
import Nav from './components/global/Nav';
import Footer from './components/global/Footer';
import { AuthContext } from "./context/authContext";
import { NavContextProvider } from "./context/navContext";
import { PageContextProvider } from "./context/pageContext";
import { DashboardContextProvider } from "./context/dashboardContext";
import { VsReportContextProvider } from "./context/vsReportContext";
import { AccessContextProvider, useAccessGate, ACCESS_GATE_STATE } from "./context/accessContext";
import { runAccessDenialProcedure } from "./context/accessDenialProcedure";
import PageSpinner from './components/global/pageSpinner';
import AccessVerificationFailed from './components/global/AccessVerificationFailed';
import initFontAwesome from './assets/fontawesome';
import { gql, useQuery, useMutation, useApolloClient } from "@apollo/client";

import AppRoutes from './Routes';

initFontAwesome();

const USER_QUERY = gql`
    query GetUser($msId: String!){
        user_by_msid(msId: $msId) {
            _id
            user_ms_oid
            user_type {
                _id
                user_type_display_name
            }
            user_role {
                _id
                user_type
                user_type_id
                user_role_display
                user_role_code
            }
            user_email
            user_fname
            user_lname
            user_job_title
            user_location
            user_office_tel
            user_mobile
            user_notes
            user_profile_image
            user_is_super_user
            user_duty_holder {
                _id
                dh_name
                dh_code
            }
            user_duty_holder_location {
                _id
                location_name
            }
            user_home_dir
            user_archived
        }
    }`;

const CREATE_ACCESS_LOG = gql`
    mutation CreateAccessLog($al: CreateAccessLog!) {
        createAccessLog(al: $al) {
            _id
            success
            status_code
            message
        }
    }
`;

const AppContent = (props) => {
  const [msId, setMsId] = useState('');
  const [accountInfo, setAccountInfo] = useState({});

  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const authContext = useContext(AuthContext);
  const { state: accessGateState, reasonCode, correlationId, retry } = useAccessGate();
  const apolloClient = useApolloClient();
  const denialHandledRef = useRef(false);
  const checkFailurePurgedRef = useRef(false);

  // The full profile lookup (and everything derived from it - authContext.user,
  // the access log) must not run before the application principal is granted -
  // no protected data may be fetched pre-grant (docs/amends/user-access-restriction
  // /tasks.md P4-3).
  const { data: userByMsIdData, refetch } = useQuery(USER_QUERY, {
    variables: { msId: msId },
    skip: accessGateState !== ACCESS_GATE_STATE.ACCESS_GRANTED || !msId,
    errorPolicy: 'all'
  });
  const [createAccessLog, { data: createAccessLogData }] = useMutation(CREATE_ACCESS_LOG);

  useEffect(() => {
    if (accounts && accounts.length > 0) {
      const msalUserAccount = accounts[0];
      setAccountInfo(msalUserAccount);
    }
  }, [accounts])

  useEffect(() => {
    if (accountInfo && accountInfo.localAccountId) {
      setMsId(accountInfo.localAccountId);
    }
  }, [accountInfo])

  useEffect(() => {
    refetch();
  }, [authContext.updateUser])

  useEffect(() => {
    if (userByMsIdData && userByMsIdData.user_by_msid) {
      // No archived-user special case here any more: the central Apollo guard
      // (api/server/graphql/resolvers/Shared/EnforceAccess.js) and this gate
      // already keep an archived user out of ACCESS_GRANTED entirely, so this
      // query is never reached for one - archived-user logout is handled by
      // the ACCESS_DENIED branch below, uniformly with every other denial reason.
      authContext.setUser(userByMsIdData.user_by_msid);
      handleAccessLog(userByMsIdData.user_by_msid, isAuthenticated);
    }
  }, [userByMsIdData])

  useEffect(() => {
    if (createAccessLogData && createAccessLogData.createAccessLog) {
      //const { success, message } = createAccessLogData.createAccessLog;
      // if (!success) {
      //   alert(`${message}`);
      // }
    }
  }, [createAccessLogData])

  // Confirmed policy denial: run the full denial procedure once (cache purge,
  // app-owned storage clear, MSAL logout to the public /access-denied route -
  // docs/amends/user-access-restriction/tasks.md P5-4 through P5-7). Guarded
  // by a ref so it only fires once even if this state is re-entered before
  // the logout redirect navigates away.
  useEffect(() => {
    if (accessGateState === ACCESS_GATE_STATE.ACCESS_DENIED && !denialHandledRef.current) {
      denialHandledRef.current = true;
      runAccessDenialProcedure({ apolloClient, msalInstance: instance, reasonCode, correlationId, account: accounts && accounts[0] });
    }
  }, [accessGateState])

  // ACCESS_CHECK_FAILED is NOT a confirmed denial - no forced logout or
  // storage clearing - but remediation-plan.md §4.6 still calls for purging
  // protected in-memory state so no stale business data lingers in the cache
  // while the user decides whether to retry or sign out themselves.
  useEffect(() => {
    if (accessGateState === ACCESS_GATE_STATE.ACCESS_CHECK_FAILED && !checkFailurePurgedRef.current) {
      checkFailurePurgedRef.current = true;
      apolloClient.clearStore().catch(() => {});
    }
    if (accessGateState !== ACCESS_GATE_STATE.ACCESS_CHECK_FAILED) {
      checkFailurePurgedRef.current = false;
    }
  }, [accessGateState])

  const handleAccessLog = (user, isAuthenticated) => {
    if (user) {
      const { _id = "", user_email = "", user_type: { user_type_display_name = "" } } = user;
      const payload = {
        al_user_id: _id,
        al_user_type: user_type_display_name,
        al_user_ip_address: "Not Available",
        al_user_email: user_email,
        al_login_status: isAuthenticated ? "Success" : "Fail",
        al_added_date: new Date()
      };
      createAccessLog({ variables: { al: payload } });
    };
  }

  const logoutHandler = () => {
    localStorage.removeItem('dropDownSelection');
    instance.logoutRedirect({
      postLogoutRedirectUri: "/",
    });
  }

  // Exhaustive gate: Header/Nav/AppRoutes/Footer are only reachable via the
  // final return below, once accessGateState === ACCESS_GRANTED. Every other
  // state returns its own screen and stops here - no protected component is
  // reachable through any other path.
  if (accessGateState === ACCESS_GATE_STATE.VERIFYING_ACCESS) {
    return <PageSpinner displayText="Verifying access..." />;
  }

  if (accessGateState === ACCESS_GATE_STATE.ACCESS_DENIED) {
    // No SystemAccessDenied flash here: the denial procedure (useEffect above)
    // no longer does a network round-trip to Microsoft (that's what the old
    // logoutRedirect-based version was doing) - it's just local cache/storage
    // clearing followed by an immediate navigation to /access-denied, which
    // is where the real, actionable denial screen (with its own sign-out/
    // sign-in control) lives. Showing a full page with its own button here,
    // for a state that's about to disappear on its own in a moment, is just
    // confusing - a spinner matches what's actually happening (briefly busy,
    // then gone).
    return <PageSpinner displayText="Redirecting..." />;
  }

  if (accessGateState === ACCESS_GATE_STATE.ACCESS_CHECK_FAILED) {
    return <AccessVerificationFailed reasonCode={reasonCode} correlationId={correlationId} onRetry={retry} onSignOut={logoutHandler} />;
  }

  return (
    <>
      <Header logoutHandler={logoutHandler} accountInfo={accountInfo} />
      <NavContextProvider>
        <PageContextProvider>
          <DashboardContextProvider>
            <VsReportContextProvider>
              <div className="flex-row lg:flex">
                <Nav />
                <AppRoutes />
              </div>
            </VsReportContextProvider>
          </DashboardContextProvider>
        </PageContextProvider>
      </NavContextProvider>
      <Footer />
    </>
  )
}

const App = (props) => (
  <AccessContextProvider>
    <AppContent {...props} />
  </AccessContextProvider>
);

export default App;